import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  css,
  Grid,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import GlobeOffIcon from '@/assets/icons/status/globe-off.svg?react';
import GlobeOnIcon from '@/assets/icons/status/globe-on.svg?react';
import Close from '@/assets/icons/tree-map/close.svg?react';
import Open from '@/assets/icons/tree-map/open.svg?react';
import { HoverActions, HoverActionsProps } from '@/components/form-elements/HoverActions';
import { QueryKeys } from '@/services/QueryKeys';
import { AdminLevel, AdminLevelsService, AdminLevelType } from '@/services/api';

const StyledAccordion = styled(Accordion)(
  ({ theme }) => css`
    &::before {
      content: none;
    }

    &:not(:last-child) {
      margin-bottom: ${theme.spacing(1)};
    }
  `,
);

const StyledAccordionSummary = styled(AccordionSummary)(
  ({ theme }) => css`
    background-color: ${theme.palette.divider};
    height: ${theme.spacing(7)};
    max-width: min(50vw, ${theme.spacing(70)});

    &:not(:last-child) {
      margin-bottom: ${theme.spacing(1)};
    }
  `,
);

const StyledTypography = styled(Typography)(
  ({ theme }) => css`
    color: ${theme.palette.text.disabled};
  `,
);

const StyledAccordionDetails = styled(AccordionDetails)(
  ({ theme }) => css`
    padding: 0 0 0 ${theme.spacing(12)};
  `,
);

type AdminLevelNodeAccordionProps = {
  children: ReactNode;
  adminLevel: AdminLevel;
  title: string;
} & HoverActionsProps;

export function AdminLevelNodeAccordion({ children, title, actions, adminLevel }: AdminLevelNodeAccordionProps) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (value: boolean) =>
      AdminLevelsService.update({ id: adminLevel.id, requestBody: { publication: value } }),

    onSuccess: async () => {
      // invaldidate query
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.adminLevels.allFilter({ type: [AdminLevelType.GOVERNMENT_DISTRICT], depth: 2 }),
      });
    },
  });

  return (
    <StyledAccordion
      TransitionProps={{ unmountOnExit: true }}
      expanded={expanded}
      onChange={() => setExpanded((prevState) => !prevState)}
      disableGutters
    >
      <StyledAccordionSummary className="hover-actions-trigger">
        <Grid container alignItems="center">
          <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
            {expanded ? <Close /> : <Open />}
          </Grid>
          <Grid item marginLeft={2}>
            {actions.length > 0 ? <HoverActions title={title} actions={actions} /> : <Typography>{title}</Typography>}
          </Grid>

          <Grid item marginLeft="auto">
            <Stack direction="row" alignItems="center" gap={1}>
              <StyledTypography>{t(`AdminLevelType.${adminLevel.type}`)}</StyledTypography>
              {!adminLevel.children && (
                <Tooltip
                  title={
                    adminLevel.publication
                      ? 'Im öffentlichen Dashboard sichtbar'
                      : 'Im öffentlichen Dashboard nicht sichtbar'
                  }
                >
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      mutate(!adminLevel.publication);
                    }}
                  >
                    {adminLevel.publication ? <GlobeOnIcon width={16} /> : <GlobeOffIcon width={16} />}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Grid>
        </Grid>
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children}</StyledAccordionDetails>
    </StyledAccordion>
  );
}
